import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRoleDiscounts, updateRoleDiscount, RoleDiscount } from '../../services/supabase';
import toast from 'react-hot-toast';

const AdminRoleDiscounts = () => {
    const queryClient = useQueryClient();
    const [editingId, setEditingId] = useState<string | null>(null);
    const [discountValues, setDiscountValues] = useState<Record<string, number>>({});

    // Получаем данные о скидках
    const { data: discounts, isLoading } = useQuery({
        queryKey: ['admin', 'role-discounts'],
        queryFn: async () => {
            const { data, error } = await getRoleDiscounts();
            if (error) {
                console.error('Ошибка при получении скидок по ролям:', error);
                throw error;
            }
            
            console.log('Получены данные о скидках по ролям:', data);
            
            // Инициализация локального состояния значений скидок
            const initialValues: Record<string, number> = {};
            data?.forEach((discount: RoleDiscount) => {
                initialValues[discount.id] = discount.discount_percent;
            });
            setDiscountValues(initialValues);
            
            return data as RoleDiscount[];
        },
        staleTime: 0, // Отключаем кеширование
    });

    // Мутация для обновления скидки
    const updateDiscountMutation = useMutation({
        mutationFn: ({ role, discountPercent }: { role: string; discountPercent: number }) => {
            return updateRoleDiscount(role, discountPercent);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'role-discounts'] });
            toast.success('Скидка успешно обновлена');
            setEditingId(null);
        },
        onError: (error: any) => {
            console.error('Ошибка при обновлении скидки:', error);
            toast.error('Не удалось обновить скидку');
        }
    });

    // Обработчик изменения значения скидки
    const handleDiscountChange = (id: string, value: string) => {
        // Проверка на числовое значение в допустимом диапазоне
        const numValue = parseInt(value);
        
        if (isNaN(numValue)) {
            return;
        }
        
        if (numValue < 0) {
            setDiscountValues({ ...discountValues, [id]: 0 });
        } else if (numValue > 100) {
            setDiscountValues({ ...discountValues, [id]: 100 });
        } else {
            setDiscountValues({ ...discountValues, [id]: numValue });
        }
    };

    // Обработчик сохранения изменений
    const handleSaveDiscount = (discount: RoleDiscount) => {
        const newDiscountValue = discountValues[discount.id] || 0;
        updateDiscountMutation.mutate({ 
            role: discount.role, 
            discountPercent: newDiscountValue 
        });
    };

    // Обработчик отмены редактирования
    const handleCancelEdit = (discount: RoleDiscount) => {
        setDiscountValues({ 
            ...discountValues, 
            [discount.id]: discount.discount_percent 
        });
        setEditingId(null);
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            {/* Хлебные крошки */}
            <div className="flex items-center text-sm text-gray-500 mb-4">
                <Link to="/admin" className="hover:text-primary">
                    Панель администратора
                </Link>
                <span className="mx-2">→</span>
                <span className="text-gray-700 font-medium">Управление скидками</span>
            </div>

            {/* Заголовок страницы */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold text-gray-800">Управление скидками по ролям</h1>
            </div>

            {/* Описание */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-6">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-blue-800">
                            Здесь вы можете установить скидки для различных ролей пользователей. Скидка будет автоматически применяться при создании бронирования пользователем с соответствующей ролью.
                        </p>
                    </div>
                </div>
            </div>

            {/* Таблица скидок */}
            {isLoading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Роль
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Текущая скидка
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Новое значение
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Действия
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {discounts?.map((discount) => (
                                <tr key={discount.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {discount.role_display_name || discount.role}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {discount.role}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            discount.discount_percent > 0 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {discount.discount_percent}%
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {editingId === discount.id ? (
                                            <div className="flex items-center">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    className="max-w-[80px] px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
                                                    value={discountValues[discount.id] || 0}
                                                    onChange={(e) => handleDiscountChange(discount.id, e.target.value)}
                                                />
                                                <span className="ml-2 text-sm text-gray-600">%</span>
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-500">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        {editingId === discount.id ? (
                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    type="button"
                                                    className="text-green-600 hover:text-green-900"
                                                    onClick={() => handleSaveDiscount(discount)}
                                                    disabled={updateDiscountMutation.isPending}
                                                >
                                                    {updateDiscountMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-red-600 hover:text-red-900"
                                                    onClick={() => handleCancelEdit(discount)}
                                                >
                                                    Отмена
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="text-primary-600 hover:text-primary-900"
                                                onClick={() => setEditingId(discount.id)}
                                            >
                                                Изменить
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminRoleDiscounts; 